/**
 * =============================================================================
 * SEMANTIC ADMIN LOCATIONS (ADR-025 blueprint §3.1 — B-1)
 * =============================================================================
 *
 * "Semantic locations first, mechanism second." An `AdminLocation` is a typed,
 * SERIALIZABLE value naming a place in the admin — the stable contract that
 * BigCommerce App Extensions (inbound app-relative deep links) or any future
 * host adapter can map into, even though v1 writes NO URLs (§3.1.6: no
 * marketed deep links; Overview tiles dispatch locations, never URLs).
 *
 * The navigation MECHANISM behind these values is the typed reducer in
 * `AdminShell` (OD-2, ratified 2026-07-12 after the E3 evidence sprint: the
 * app does not own the iframe URL; `location.hash`/`pushState` create
 * Back-trapping joint history entries — measured, not assumed. NEVER
 * `BrowserRouter`; any future URL reflection must be `replaceState`-only).
 *
 * CONTRACT (pinned by test — §3.1.7 / M8):
 *   - `resolveLocation(serializeLocation(loc))` is lossless for every valid
 *     location;
 *   - `resolveLocation(input: unknown)` NEVER throws and fail-safes to
 *     `{ section: 'questions' }` (the OD-14 landing) on garbage; a valid
 *     section with invalid optional fields keeps the section and drops the
 *     bad fields.
 *
 * `QueueView` / `MediaView` name the filtered queue views the B-3 Overview
 * tiles navigate to (§3.2: each tile targets the SAME predicate its count is
 * computed from). They are semantic wire values — deliberately decoupled from
 * DB status enums so the serialized contract never leaks storage details.
 * B-3/B-4 may extend the unions additively.
 * =============================================================================
 */

/** Filtered views of the Questions queue reachable as navigation targets. */
export type QueueView = 'pending' | 'needsReview';

/** Filtered views of the Media review queue reachable as navigation targets. */
export type MediaView = 'pendingReview';

/**
 * The seven §4 sections. `overview` exists in the CONTRACT from B-1 (so a
 * serialized overview location resolves losslessly), but its section content
 * ships in B-3 — the shell falls back to the landing for sections its
 * registry has not (yet) registered.
 */
export const ADMIN_SECTION_IDS = [
  'overview',
  'questions',
  'media',
  'analytics',
  'support',
  'audit',
  'settings',
] as const;

export type AdminSectionId = (typeof ADMIN_SECTION_IDS)[number];

export type AdminLocation =
  | { section: 'overview' }
  | { section: 'questions'; view?: QueueView; questionId?: string }
  | { section: 'media'; view?: MediaView }
  | { section: 'analytics' }
  | { section: 'support' }
  | { section: 'audit' }
  | { section: 'settings' };

/** Last semantic location retained for each section under shell keep-alive. */
export type AdminSectionLocations = {
  [Section in AdminSectionId]?: Extract<AdminLocation, { section: Section }>;
};

/**
 * Repeatable explicit filtered destinations under keep-alive navigation.
 * Each consumer owns a monotonic slot so an intent for one hidden section can
 * never clear or replay another section's retained state.
 */
export type AdminFilteredViewIntents = Readonly<{
  questions: { view: QueueView; nonce: number } | null;
  media: { view: MediaView; nonce: number } | null;
}>;

/** OD-14: landing = Questions (Overview promotion is the M16 gate, not B-1). */
export const DEFAULT_ADMIN_LOCATION: AdminLocation = { section: 'questions' };

const QUEUE_VIEWS: readonly QueueView[] = ['pending', 'needsReview'];
const MEDIA_VIEWS: readonly MediaView[] = ['pendingReview'];
const SECTION_ID_SET: ReadonlySet<string> = new Set(ADMIN_SECTION_IDS);

/**
 * Question ids are opaque strings on this contract (the worker owns the id
 * grammar). Bound only to a sane serializable shape: non-empty, no control
 * characters (checked by char code — no control chars in a regex literal),
 * capped length. Anything else is dropped (fail-safe, §3.1).
 */
function isPlausibleQuestionId(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0 || value.length > 128) {
    return false;
  }
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) {
      return false;
    }
  }
  return true;
}

/**
 * Serialize a location to a compact, stable JSON string (the App-Extension /
 * host-adapter wire form). Keys are emitted in a fixed order so equal
 * locations always serialize identically.
 */
export function serializeLocation(location: AdminLocation): string {
  const out: Record<string, string> = { section: location.section };
  if (location.section === 'questions') {
    if (location.view !== undefined) out.view = location.view;
    if (location.questionId !== undefined) out.questionId = location.questionId;
  } else if (location.section === 'media') {
    if (location.view !== undefined) out.view = location.view;
  }
  return JSON.stringify(out);
}

/**
 * Resolve ANY untrusted input (a serialized string, a parsed object, or
 * garbage) to a valid `AdminLocation`. Never throws.
 *
 * Fail-safe ladder (§3.1):
 *   - unparseable / non-object / unknown `section` → `{ section: 'questions' }`;
 *   - known section + invalid optional field(s) → the section, bad fields
 *     dropped;
 *   - optional fields are only honoured on the sections that declare them.
 */
export function resolveLocation(input: unknown): AdminLocation {
  let candidate: unknown = input;

  if (typeof candidate === 'string') {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return { ...DEFAULT_ADMIN_LOCATION };
    }
  }

  if (candidate === null || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return { ...DEFAULT_ADMIN_LOCATION };
  }

  const record = candidate as Record<string, unknown>;
  const section = record.section;
  if (typeof section !== 'string' || !SECTION_ID_SET.has(section)) {
    return { ...DEFAULT_ADMIN_LOCATION };
  }

  switch (section as AdminSectionId) {
    case 'questions': {
      const location: AdminLocation = { section: 'questions' };
      if (QUEUE_VIEWS.includes(record.view as QueueView)) {
        location.view = record.view as QueueView;
      }
      if (isPlausibleQuestionId(record.questionId)) {
        location.questionId = record.questionId;
      }
      return location;
    }
    case 'media': {
      const location: AdminLocation = { section: 'media' };
      if (MEDIA_VIEWS.includes(record.view as MediaView)) {
        location.view = record.view as MediaView;
      }
      return location;
    }
    default:
      return { section: section as Exclude<AdminSectionId, 'questions' | 'media'> };
  }
}
